import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  BillingReceiptError,
  OrderReceiptData,
} from "../../interfaces/billing-order-details.interface";
import { getOrderReceipt } from "../../services/billing-order-details/order-receipt.service";

const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatMoney = (amount: number) => money.format(Number.isFinite(amount) ? amount : 0);
const formatDate = (dateValue?: string) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};
const positive = (amount: number) => amount > 0;

const StatusMessage = ({ title, detail }: { title: string; detail: string }) => (
  <main className="billing-receipt-page"><section className="billing-receipt-message" role="alert"><i className="pi pi-shopping-cart" aria-hidden="true" /><h1>{title}</h1><p>{detail}</p></section></main>
);

const OrderReceipt = () => {
  const { invoicenumber } = useParams<{ invoicenumber: string }>();
  const orderno = invoicenumber?.replace(/^order-/i, "");
  const [data, setData] = useState<OrderReceiptData | null>(null);
  const [error, setError] = useState<BillingReceiptError | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const oldTitle = document.title;
    const robots = document.querySelector('meta[name="robots"]') || document.createElement("meta");
    const wasInHead = robots.parentElement !== null;
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    if (!wasInHead) document.head.appendChild(robots);
    document.title = "Farm2Me Order Receipt";
    return () => { document.title = oldTitle; if (!wasInHead) robots.remove(); };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    if (!orderno || !/^\d{1,18}$/.test(orderno)) {
      setError(new BillingReceiptError("not-found"));
      setLoading(false);
      return () => { mounted = false; };
    }
    getOrderReceipt(orderno).then((receipt) => { if (mounted) setData(receipt); }).catch((reason) => { if (mounted) setError(reason); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [orderno]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <main className="billing-receipt-page billing-receipt-loading" aria-busy="true"><ProgressSpinner aria-label="Loading order receipt" /><p>Loading order receipt</p></main>;
  if (!data || error || data.receiptType !== "order") {
    if (error?.reason === "not-found") return <StatusMessage title="Order receipt not found" detail="Check the order number and try again." />;
    return <StatusMessage title="Order receipt unavailable" detail="Please try again in a little while." />;
  }

  const summary = data.summary;
  const items = expanded ? data.items : data.items.slice(0, 2);
  const payment = data.payment?.mode || data.payment?.displayText?.replace(/^paid via\s+/i, "");

  return (
    <main className="billing-receipt-page">
      <section className="billing-receipt-card" aria-labelledby="order-receipt-title">
        <header className="billing-receipt-header">
          <div className="billing-receipt-brand">
            <img src={process.env.PUBLIC_URL + "/assests/images/DesiriLogo.png"} alt="Desiri" />
          </div>
          <h1 id="order-receipt-title">ORDER RECEIPT</h1>
          <div className="billing-receipt-date"><span>Order date</span><strong>{formatDate(data.orderDate) || "-"}</strong></div>
        </header>
        <p className="billing-receipt-invoice">Order #{data.orderNumber}</p>

        <section className="billing-receipt-details">
          {data.customer?.name && <div className="billing-receipt-customer-block"><span>CUSTOMER</span><div className="billing-receipt-customer-value"><small>{data.customer.name}</small></div></div>}
          {data.customer?.maskedMobile && <div className="billing-receipt-store-block"><span>PHONE</span><div className="billing-receipt-store-value"><small>{/^\+/.test(data.customer.maskedMobile) ? data.customer.maskedMobile : `+91 ${data.customer.maskedMobile}`}</small></div></div>}
        </section>
        {payment && <div className="billing-receipt-payment"><i className="pi pi-check-circle" aria-hidden="true" /><span>PAYMENT METHOD</span><strong>{payment}</strong></div>}

        <section className="billing-receipt-summary" aria-label="Order summary">
          <div><span>ITEMS</span><strong>{summary.itemCount}</strong></div>
          <div><span>TOTAL UNITS</span><strong>{summary.totalUnits}</strong></div>
          <div><span>MRP / TAXABLE AMOUNT</span><strong>{formatMoney(summary.taxableAmount)}</strong></div>
          <div><span>TOTAL DISCOUNTS</span><strong className="billing-receipt-discount">-{formatMoney(summary.discountAmount)}</strong></div>
          {positive(summary.couponDiscountAmount) && <div><span>COUPON DISCOUNT</span><strong className="billing-receipt-discount">-{formatMoney(summary.couponDiscountAmount)}</strong></div>}
          {positive(summary.rewardPointDiscountAmount) && <div><span>REWARD POINTS DISCOUNT</span><strong className="billing-receipt-reward">-{formatMoney(summary.rewardPointDiscountAmount)}</strong></div>}
          {positive(summary.deliveryCharge) && <div><span>DELIVERY CHARGES</span><strong>{formatMoney(summary.deliveryCharge)}</strong></div>}
          {positive(summary.taxAmount) && <div><span>TAX / GST</span><strong>{formatMoney(summary.taxAmount)}</strong></div>}
          {positive(summary.paymentGatewayCharges) && <div><span>PAYMENT GATEWAY CHARGES</span><strong>{formatMoney(summary.paymentGatewayCharges)}</strong></div>}
          <div className="billing-receipt-grand-total"><span>TOTAL AMOUNT PAID</span><strong>{formatMoney(summary.totalAmountPaid)}</strong></div>
          {positive(summary.rewardPointsEarned) && <div className="billing-receipt-earned"><span>REWARD POINTS EARNED</span><strong>{summary.rewardPointsEarned}</strong></div>}
        </section>

        <section className="billing-receipt-items" aria-labelledby="order-receipt-items-title">
          <div className="billing-receipt-items-heading"><h2 id="order-receipt-items-title">ITEMS</h2><span>{data.items.length}</span></div>
          {items.map((item, index) => <article className="billing-receipt-item" key={`${item.name}-${index}`}><div className="billing-receipt-item-image">{item.imageUrl ? <img src={item.imageUrl} alt="" /> : <i className="pi pi-shopping-bag" aria-hidden="true" />}</div><div className="billing-receipt-item-name"><strong>{item.name}</strong><small>{item.quantity}{item.unit ? ` ${item.unit}` : ""}</small></div><strong>{formatMoney(item.lineTotal)}</strong></article>)}
          {data.items.length > 2 && <button className="billing-receipt-view-button" type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>{expanded ? "HIDE ITEMS" : "VIEW ALL ITEMS"}<i className={`pi pi-chevron-${expanded ? "up" : "down"}`} aria-hidden="true" /></button>}
        </section>
      </section>
    </main>
  );
};

export default OrderReceipt;